import { fromJS, List, Map } from 'immutable'
import { handleActions } from 'redux-actions'
import { JOBS_TRIGGERS } from 'constants/appRouting'
import Psfs from 'lib/psfs/Psfs'
import {
  FIELD_DUE_DATE,
  FIELD_DUE_DATE_DAYS,
  FIELD_DUE_DATE_TIME,
  FIELD_NAME,
  FIELD_SITE,
  FIELD_SITES,
  FIELD_START_DATE,
  FIELD_START_DATE_INPUT,
  FIELD_START_DELAY,
  FIELD_START_DELAY_DAYS,
  FIELD_TRIGGER_ACTION,
  FIELD_TRIGGER_INPUTS,
  FIELD_TRIGGER_JOB_WORKFLOW,
  FIELD_TRIGGER_TYPE,
  FIELD_TRIGGERED_JOB_WORKFLOW,
  VALUE_NEVER,
  VALUE_IS_COMPLETED,
  VALUE_NO_DELAY,
  VALUE_START,
  VALUE_WHEN_PARENT_COMPLETED,
  REGULAR_REPEAT_SCHEDULE,
  REPEAT,
  START_TIME,
  TRIGGER_ASSIGNMENTS,
} from 'constants/jobsTriggers'
import ActionHelper from 'helpers/action'
import { NEVER } from 'constants/managementUI'

// constants
export const CLEAR_STORE = 'jobsTriggers/CLEAR_STORE'
export const CREATE_OR_UPDATE_TRIGGER_RULE =
  'jobsTriggers/CREATE_OR_UPDATE_TRIGGER_RULE'
export const SET_IS_FETCHING = 'jobsTriggers/SET_IS_FETCHING'
export const FETCH_TRIGGER_INPUTS = 'jobsTriggers/FETCH_TRIGGER_INPUTS'
export const [FETCH_TRIGGER_INPUTS_SUCCESS, FETCH_TRIGGER_INPUTS_FAILURE] = [
  `${FETCH_TRIGGER_INPUTS}_SUCCESS`,
  `${FETCH_TRIGGER_INPUTS}_FAILURE`,
]
export const FETCH_TRIGGER_EXISTS = 'jobsTriggers/FETCH_TRIGGER_EXISTS'
export const SET_PSFS = 'jobsTriggers/SET_PSFS'
export const SET_TRIGGER_TABLE_IS_LOADING =
  'jobsTriggers/SET_TRIGGER_TABLE_IS_LOADING'
export const FETCH_TRIGGER_TABLE_DATA = 'jobsTriggers/FETCH_TRIGGER_TABLE_DATA'
export const SUCCESS_FETCH_TRIGGER_TABLE_DATA =
  'jobsTriggers/SUCCESS_FETCH_TRIGGER_TABLE_DATA'
export const FAILURE_FETCH_TRIGGER_TABLE_DATA =
  'jobsTriggers/FAILURE_FETCH_TRIGGER_TABLE_DATA'
export const DELETE_TRIGGER_RULE = 'jobsTriggers/DELETE_TRIGGER_RULE'
export const TOGGLE_TRIGGER_RULE = 'jobsTriggers/TOGGLE_TRIGGER_RULE'
export const DUPLICATE_TRIGGER_RULE = 'jobsTriggers/DUPLICATE_TRIGGER_RULE'
export const SET_FORM_DATA = 'jobsTriggers/SET_FORM_DATA'
export const FETCH_TRIGGER_RULE = 'jobsTriggers/FETCH_TRIGGER_RULE'
export const FETCH_TRIGGER_DEFAULT_ASSIGNMENTS =
  'jobsTriggers/FETCH_TRIGGER_DEFAULT_ASSIGNMENTS'
export const SUCCESS_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS =
  'jobsTriggers/SUCCESS_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS'
export const FAILURE_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS =
  'jobsTriggers/FAILURE_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS'
export const FETCH_HIERARCHICAL_SITE_GROUPS =
  'jobsTriggers/FETCH_HIERARCHICAL_SITE_GROUPS'
export const SUCCESS_FETCH_HIERARCHICAL_SITE_GROUPS =
  'jobsTriggers/SUCCESS_FETCH_HIERARCHICAL_SITE_GROUPS'
export const FAILURE_FETCH_HIERARCHICAL_SITE_GROUPS =
  'jobsTriggers/FAILURE_FETCH_HIERARCHICAL_SITE_GROUPS'
export const SET_HIERARCHICAL_SITE_GROUPS_LOADING =
  'jobsTriggers/SET_HIERARCHICAL_SITE_GROUPS_LOADING'

// actions
export const clearStore = ActionHelper.createActionObj(CLEAR_STORE)
export const fetchTriggerInputs = ActionHelper.createActionObj(
  FETCH_TRIGGER_INPUTS,
)
export const fetchTriggerExists = ActionHelper.createActionObj(
  FETCH_TRIGGER_EXISTS,
)
export const createOrUpdateTriggerRule = ActionHelper.createActionObj(
  CREATE_OR_UPDATE_TRIGGER_RULE,
)
export const setTablePsfs = ActionHelper.createActionObj(SET_PSFS)
export const setIsLoading = ActionHelper.createActionObj(
  SET_TRIGGER_TABLE_IS_LOADING,
)
export const fetchTriggerTableData = ActionHelper.createActionObj(
  FETCH_TRIGGER_TABLE_DATA,
)
export const fetchTriggerDefaultAssignments = ActionHelper.createActionObj(
  FETCH_TRIGGER_DEFAULT_ASSIGNMENTS,
)
export const deleteTriggerRule = ActionHelper.createActionObj(
  DELETE_TRIGGER_RULE,
)
export const toggleTriggerRule = ActionHelper.createActionObj(
  TOGGLE_TRIGGER_RULE,
)
export const duplicateTriggerRule = ActionHelper.createActionObj(
  DUPLICATE_TRIGGER_RULE,
)
export const clearTriggerInputs = ActionHelper.createActionObj(
  FETCH_TRIGGER_INPUTS_FAILURE,
)
export const setFormData = ActionHelper.createActionObj(SET_FORM_DATA)
export const fetchTriggerRule = ActionHelper.createActionObj(FETCH_TRIGGER_RULE)
export const fetchHierarchicalSiteGroups = ActionHelper.createActionObj(
  FETCH_HIERARCHICAL_SITE_GROUPS,
)

export const DEFAULT_FORM_DATA = {
  [FIELD_TRIGGER_JOB_WORKFLOW]: null,
  [FIELD_TRIGGERED_JOB_WORKFLOW]: null,
  [FIELD_NAME]: {},
  [FIELD_TRIGGER_TYPE]: VALUE_IS_COMPLETED,
  [FIELD_TRIGGER_ACTION]: VALUE_START,
  [FIELD_TRIGGER_INPUTS]: [],
  [FIELD_SITE]: null,
  [FIELD_SITES]: [],
  [FIELD_START_DATE]: VALUE_WHEN_PARENT_COMPLETED,
  [FIELD_START_DATE_INPUT]: null,
  [FIELD_START_DELAY]: VALUE_NO_DELAY,
  [FIELD_START_DELAY_DAYS]: null,
  [REPEAT]: NEVER,
  [FIELD_DUE_DATE]: VALUE_NEVER,
  [FIELD_DUE_DATE_DAYS]: null,
  [FIELD_DUE_DATE_TIME]: null,
  [REGULAR_REPEAT_SCHEDULE]: {},
  [START_TIME]: null,
  [TRIGGER_ASSIGNMENTS]: [],
}

// store
const initialState = () =>
  fromJS({
    isLoading: false,
    isFetching: false,
    formData: Map(DEFAULT_FORM_DATA),
    triggerInputs: List([]),
    triggerDefaultAssignments: List([]),
    hierarchicalSiteGroups: List([]),
    isLoadingHierarchicalSiteGroups: false,
    triggerTableData: Map({
      data: [],
      count: 0,
    }),
    tablePsfs: new Psfs(JOBS_TRIGGERS),
  })

const jobsTriggers = handleActions(
  {
    [FETCH_TRIGGER_INPUTS_SUCCESS]: (state, action) =>
      state.set('triggerInputs', List(action.payload)),
    [SET_IS_FETCHING]: (state, action) =>
      state.set('isFetching', action.payload),
    [FETCH_TRIGGER_INPUTS_FAILURE]: state =>
      state.set('triggerInputs', List([])),
    [SET_PSFS]: (state, action) => state.set('tablePsfs', action.payload),
    [SET_TRIGGER_TABLE_IS_LOADING]: (state, action) =>
      state.set('isLoading', Boolean(action.payload)),
    [SUCCESS_FETCH_TRIGGER_TABLE_DATA]: (state, action) =>
      state.set('triggerTableData', Map(action.payload)),
    [SUCCESS_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS]: (state, action) =>
      state.set('triggerDefaultAssignments', List(action.payload)),
    [SET_FORM_DATA]: (state, action) => {
      const { dueDateTime, startTime } = action.payload
      const formData = {
        ...action.payload,
      }

      if (startTime && typeof startTime === 'string') {
        formData.startTime = new Date().setHours(...startTime.split(':'))
      }

      if (dueDateTime) {
        if (typeof dueDateTime === 'string') {
          const splittedTime = dueDateTime.split(':')

          formData.dueDateTime = new Date().setHours(...splittedTime)
        } else {
          formData.dueDateTime = new Date(dueDateTime)
        }
      }

      return state.set('formData', Map(formData))
    },
    [FAILURE_FETCH_TRIGGER_TABLE_DATA]: state =>
      state.set(
        'triggerTableData',
        Map(initialState().get('triggerTableData')),
      ),
    [FAILURE_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS]: state =>
      state.set(
        'triggerDefaultAssignments',
        Map(initialState().get('triggerDefaultAssignments')),
      ),
    [SET_HIERARCHICAL_SITE_GROUPS_LOADING]: (state, action) =>
      state.set('isLoadingHierarchicalSiteGroups', Boolean(action.payload)),
    [SUCCESS_FETCH_HIERARCHICAL_SITE_GROUPS]: (state, action) =>
      state.set('hierarchicalSiteGroups', List(action.payload)),
    [FAILURE_FETCH_HIERARCHICAL_SITE_GROUPS]: state =>
      state.set(
        'hierarchicalSiteGroups',
        List(initialState().get('hierarchicalSiteGroups')),
      ),
    [CLEAR_STORE]: () => initialState(),
  },
  initialState(),
)

export default jobsTriggers
